<template>
  <div>
    <h4 class="tw-text-[#6d7287] tw-text-base tw-mb-5">{{ t('sidebar.items.notifications') }}</h4>
    <Card>
      <CardContent class="tw-pt-4">
        <Tabs :default-value="activeTab" @update:model-value="onTabChange">
          <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <TabsList>
              <TabsTrigger value="unread">
                {{ t('notifications.titles.unread_tab') }}
              </TabsTrigger>
              <TabsTrigger value="read">
                {{ t('notifications.titles.read_tab') }}
              </TabsTrigger>
            </TabsList>

            <div class="tw-flex tw-items-center tw-gap-4">
              <Input
                :placeholder="t('common.data.search')"
                @input="searchNotificationsList(($event as any).target.value)"
              />

              <Button @click="markAllAsRead">
                {{ t('notifications.titles.mark_all_read') }}
              </Button>
            </div>
          </div>
        </Tabs>

        <ul>
          <template v-if="notificationsListInPage.length">
            <li
              v-for="notification in notificationsListInPage"
              :key="notification.id"
              class="notification-item"
              @click="performNotificationAction(notification)"
            >
              <div
                class="notification-icon"
                :class="{
                  'not-read': !notification.read,
                }"
              >
                <i class="icon-document"></i>
              </div>

              <div class="notification-info">
                <span class="title" :title="notification.title">
                  {{ notification.title }}
                </span>

                <span class="content" :title="notification.text">
                  {{ notification.text }}
                </span>
              </div>

              <div class="notification-action">
                <span class="time">
                  {{ moment(notification.created_on).fromNow() }}
                </span>

                <div v-if="!notification.read" class="mark-as-read">
                  <a @click.stop="markAsRead(notification.id)">
                    {{ t('notifications.titles.mark_read') }}
                  </a>
                </div>
              </div>
            </li>
          </template>

          <template v-else>
            <li>
              <span class="tw-flex tw-mt-2 tw-justify-center">
                {{ t('notifications.data.no_data') }}
              </span>
            </li>
          </template>
        </ul>
      </CardContent>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { Notification } from '@/_helpers/interfaces/notifications/notification.interface';
import { useNotificationsStore } from '@/stores/notifications.store';
import { debounce } from 'lodash';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const { notificationsListInPage } = storeToRefs(useNotificationsStore());
const { getNotificationsList, markAsRead, markAllAsRead } = useNotificationsStore();
const router = useRouter();
const route = useRoute();

const activeTab = computed(() => (route.query.status === 'read' ? 'read' : 'unread'));

const onTabChange = (value: string | number) => {
  if (value === 'read') {
    router.push({ name: 'notification-center', query: { status: 'read' } });
  } else {
    router.push({ name: 'notification-center' });
  }
};
onMounted(async () => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.notifications',
    },
  ]);
  await getNotificationsPageList();
});

const getNotificationsPageList = async (searchTerm?: string) => {
  const response = await getNotificationsList({
    page: 1,
    page_size: -1,
    ordering: '-created_on',
    search: searchTerm,
    read: router.currentRoute.value.query.status === 'read' ? true : false,
  });

  notificationsListInPage.value = [...response.results];
};

const performNotificationAction = async (notification: Notification) => {
  await markAsRead(notification.id, false);
  // remove the notification from the unread list
  notificationsListInPage.value = notificationsListInPage.value.filter(
    n => n.id !== notification.id
  );

  const routeName =
    notification.object_ct === 'purchaseorder'
      ? 'order'
      : notification.object_ct === 'payment'
      ? 'payment'
      : notification.object_ct === 'recurringinstruction'
      ? 'recurring-order'
      : '';

  if (routeName)
    router.push({
      name: routeName,
      params: {
        id: notification.object_id,
      },
    });
};

const searchNotificationsList = debounce(async function (searchTerm: string) {
  await getNotificationsPageList(searchTerm);
}, 300);

watch(
  () => router.currentRoute.value.query.status,
  async newVal => {
    await getNotificationsPageList();
  }
);
</script>

<style scoped>
.notification-item {
  display: flex;
  gap: 1rem;
  padding: 0.7rem;
  cursor: pointer;
}

.notification-item:hover {
  background-color: #e9ecef;
}

.notification-item + .notification-item {
  border-top: 1px solid #dcdcdc;
}

.notification-icon {
  position: relative;
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background-color: rgb(var(--primary-500));
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
}

.notification-icon i {
  font-size: 1rem;
  color: rgb(var(--text-primary));
}

.notification-icon.not-read::after {
  content: '';
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: red;
  position: absolute;
  top: 2px;
  right: -1px;
  border: 2px solid white;
}

.notification-info {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.notification-info .title {
  color: #000;
  font-size: 1rem;
}

.notification-info .content {
  color: #969696;
  font-size: 0.9rem;
}

.notification-action {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.notification-action .time {
  color: #969696;
  font-size: 0.8rem;
}

.notification-action .mark-as-read {
  margin-top: auto;
  font-size: 0.8rem;
}

.notification-action .mark-as-read a {
  color: rgb(var(--cta-primary));
  cursor: pointer;
}
</style>
